import { useEffect, memo, useState } from "react";
import axios from "axios";
import { NormalPeoplePicker } from "@fluentui/react/lib/Pickers";
import { InteractionType } from "@azure/msal-browser";
import { useMsalAuthentication } from "@azure/msal-react";
import './IdentityPickerExtension.css';

/**
 * Interface for default value props
 */
interface IDefaultUsersProps {
    DefaultUserMail: string;
    className?: string;
    placeholder?: string;
    DefaultUserNull?: boolean;
    onIdentityPickerChange: (data: any) => void;
}

const SingleIdentityPickerExtension = (props: IDefaultUsersProps) => {
    const [selectedIdentity, setSelectedIdentity] = useState<any | undefined>(undefined);
    const [accessToken, setAccessToken] = useState<string>('');

    const { result, error } = useMsalAuthentication(InteractionType.Popup, {
        scopes: ["User.ReadBasic.All"],
    });

    useEffect(() => {
        if (result) {
            setAccessToken(result.accessToken);
        }
        if (error) {
            console.error("useMsalAuthentication Error:", error);
        }

        if (props.DefaultUserMail && props.DefaultUserMail !== "") {
            let searchCondition = "\"userPrincipalName:" + props.DefaultUserMail + "\"";

            const loadData = async () => {
                if (result) {
                    var config = {
                        headers: {
                            'Access-Control-Allow-Origin': '*',
                            "ConsistencyLevel": "eventual",
                            'Authorization': `Bearer ${result.accessToken}`,
                        },
                    };
                    try {
                        const res = await axios.get(
                            `https://graph.microsoft.com/v1.0/users?$count=true&$search=${searchCondition}&$filter=endsWith(mail,'@microsoft.com')&$select=id,displayName,userPrincipalName`,
                            config
                        );
                        if (res && res.data && res.data.value) {
                            const userAccount = res.data.value[0];
                            const defaultIdentity = {
                                key: userAccount?.userPrincipalName,
                                text: userAccount?.displayName,
                                secondaryText: userAccount?.userPrincipalName,
                                entityId: userAccount?.userPrincipalName,
                                mail: userAccount?.userPrincipalName,
                                originDirectory: "aad",
                                originId: "",
                            };
                            setSelectedIdentity(defaultIdentity);
                        }
                    } catch (err) {
                        console.error("Get default users", err);
                    }
                }
            };

            loadData();
        } else {
            if (props.DefaultUserNull) {
                setSelectedIdentity(undefined);
            }
        }
    }, [result, error, props.DefaultUserMail, props.DefaultUserNull]);

    /**
     * Handle identity change
     * @param identity 
     */
    const onChange = (items: any[]) => {
        if (items.length == 0) {
            props.onIdentityPickerChange(undefined);
        } else {
            const identity = items[items.length - 1];
            setSelectedIdentity(identity);
            props.onIdentityPickerChange(identity?.mail);
        }
    };

    const pickerProvider = new IdentityPickerSingleProvider(accessToken);

    return (
        <NormalPeoplePicker
            onChange={onChange}
            onResolveSuggestions={pickerProvider.onFilterIdentities.bind(pickerProvider)}
            selectedItems={selectedIdentity ? [selectedIdentity] : []}
            pickerSuggestionsProps={{
                suggestionsHeaderText: 'Suggested people',
                noResultsFoundText: 'No people found',
            }}
            className="custom-suggestions-container"
            inputProps={{
                placeholder: props.placeholder ?? "Assign To",
                className: 'customNormalPeoplePicker',
            }}
        />
    );
};

export default memo(SingleIdentityPickerExtension);

/**
 * Define the IdentityPickerProvider
 */
class IdentityPickerSingleProvider {
    private personas: any[] = [];
    private accessToken: string = '';

    constructor(accessToken: string) {
        this.accessToken = accessToken;
    }

    public onFilterIdentities = async (filter: string, selectedItems?: any[]) => {
        const loadData = async () => {
            var config = {
                headers: {
                    'Access-Control-Allow-Origin': '*',
                    "ConsistencyLevel": "eventual",
                    'Authorization': `Bearer ${this.accessToken}`,
                },
            };

            if (filter === "") return Promise.resolve(null);

            return await axios.get(
                `https://graph.microsoft.com/v1.0/users?$count=true&$search="displayName:${filter}" OR "userPrincipalName:${filter}"&$filter=endsWith(mail,'@microsoft.com')&$select=id,displayName,userPrincipalName`,
                config
            );
        };

        this.personas = [];
        const item = await loadData();
        if (item && item.data.value) {
            item.data.value.forEach((personItem: any) => {
                if (!this.personas.find((existingItem) => existingItem.mail === personItem.userPrincipalName)) {
                    this.personas.push(this.getIdentityEntity(personItem.displayName, personItem.userPrincipalName));
                }
            });
        }

        return this.getPersonaList(filter, selectedItems);
    };

    private getIdentityEntity(displayName: string, mail: string) {
        return {
            key: mail,
            text: displayName,
            secondaryText: mail,
            entityId: mail,
            mail: mail,
            originDirectory: "aad",
            originId: "",
        };
    }

    private getPersonaList(filter: string, selectedItems?: any[]): any[] {
        if (!this.personas.length) {
            return [];
        }

        return this.personas.filter(
            (x) =>
                (selectedItems === undefined || selectedItems.indexOf(x) === -1) &&
                ((x.text && x.text.toLowerCase().indexOf(filter.toLowerCase()) !== -1) ||
                    (x.secondaryText && x.secondaryText.toLowerCase().indexOf(filter.toLowerCase()) !== -1))
        );
    }
}
